import { LayerProvider, LayerRenderer, LayerSwitcher, MapGL } from '@cibo/mapgl'
import { Box, Stack, useTheme } from '@mui/material'
import bbox from '@turf/bbox'
import { point } from '@turf/turf'
import chroma from 'chroma-js'
import { useMemo } from 'react'
import { Layer, NavigationControl, Source, useMap } from 'react-map-gl/dist/es5'
import { LandReportMapDataLayers } from './DataLayers'
import { LandReportMapDataLayerLegend } from './Legend'
import { LandReportMapDataLayersProps } from './types'

interface LandReportMapInteractiveProps
  extends Omit<LandReportMapDataLayersProps, 'field'>,
    Pick<Partial<LandReportMapDataLayersProps>, 'field'> {
  mapPadding?: number
}

export const LandReportMapInteractive = ({
  field,
  dataLayer,
  mapPadding = 0,
  setDataLayer,
}: LandReportMapInteractiveProps) => {
  const map = useMap()
  const bounds = !!field?.geometry ? bbox(field?.geometry) : undefined

  const theme = useTheme()
  const padding = useMemo(() => {
    const spacing = parseInt(theme.spacing(mapPadding), 10)
    return {
      top: spacing,
      right: spacing,
      bottom: spacing,
      left: spacing,
    }
  }, [mapPadding, theme])

  const textColor = useMemo(() => {
    return chroma(theme.palette.text.primary).alpha(1).hex()
  }, [theme])
  const haloColor = useMemo(() => {
    return chroma(theme.palette.background.paper).alpha(1).hex()
  }, [theme])

  const label =
    !!field?.latitude && !!field?.longitude
      ? point([field.latitude, field.longitude], {}, { id: 1 })
      : undefined

  // @ts-ignore bounds
  const handleClickSymbol = (p: any) => bounds && map.default?.fitBounds(bounds, { padding })

  return (
    <LayerProvider>
      <MapGL
        // @ts-ignore bounds
        initialViewport={{ bounds, fitBoundsOptions: { padding } }}
        interactiveLayerIds={!!label ? ['field-flag'] : []}
        onClick={handleClickSymbol}
      >
        <LayerRenderer>
          <Stack
            position="absolute"
            top={0}
            right={0}
            left={0}
            bottom={0}
            sx={{ pointerEvents: 'none', padding: 1, paddingBottom: 4 }}
            justifyContent="flex-end"
            spacing={2}
          >
            {!!field && (
              <Box sx={{ pointerEvents: 'all', alignSelf: 'flex-start' }}>
                <LandReportMapDataLayerLegend dataLayer={dataLayer} field={field} />
              </Box>
            )}
            <Box sx={{ pointerEvents: 'all', alignSelf: 'flex-start' }}>
              <LayerSwitcher />
            </Box>
          </Stack>
        </LayerRenderer>
        {!!label && (
          <Source type="geojson" data={label}>
            <Layer
              type="symbol"
              id="field-flag"
              layout={{
                'icon-image': 'pin',
                'text-field': field?.name,
                'text-offset': [0, 1.25],
                'text-anchor': 'top',
              }}
              paint={{
                'text-color': textColor,
                'text-halo-color': haloColor,
                'text-halo-width': 1,
                'text-opacity': ['interpolate', ['linear'], ['zoom'], 0, 1, 11, 1, 11.75, 0],
                'icon-opacity': ['interpolate', ['linear'], ['zoom'], 0, 1, 11, 1, 11.75, 0],
              }}
            />
          </Source>
        )}
        {!!field && (
          <LandReportMapDataLayers
            dataLayer={dataLayer}
            field={field}
            setDataLayer={setDataLayer}
          />
        )}
        <NavigationControl showZoom position="bottom-right" />
      </MapGL>
    </LayerProvider>
  )
}
